import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import apiClient from '@/lib/api/client'

const CLIENT_MGMT_URL = import.meta.env.VITE_CLIENT_MGMT_URL || 'https://client-management.sgrc.api.i2tdigital.com'
const FEATURE_ENGINE_URL = import.meta.env.VITE_FEATURE_ENGINE_URL || 'https://feature-engine.sgrc.api.i2tdigital.com'

export interface ClientSummary {
  id: string
  name: string
  industry?: string
  status: string
  relationship_id?: string
  client_tenant_id?: string
}

export interface DossierSummary {
  id: string
  client_id: string
  name: string
  reference?: string
  type: string
  status: string
  vertical?: string
  progress_percent?: string
}

export interface RelationshipSummary {
  id: string
  cabinet_tenant_id: string
  client_tenant_id: string
  status: string
  contract_id?: string
}

export interface ContractSummary {
  id: string
  title: string
  status: string
  features_granted: string[]
  modules_granted: string[]
}

export interface FeatureGateMap {
  [featureCode: string]: {
    enabled: boolean
    category: string
    reason?: string
  }
}

interface ClientContextState {
  // Active selections
  activeClientId: string | null
  activeClientName: string | null
  activeDossierId: string | null
  activeDossierName: string | null

  // Relationship & contract context for active client
  activeRelationship: RelationshipSummary | null
  activeContract: ContractSummary | null
  featureGates: FeatureGateMap

  // Loading states
  isLoadingClient: boolean
  isLoadingFeatures: boolean

  // Actions
  setActiveClient: (clientId: string, clientName?: string) => Promise<void>
  setActiveDossier: (dossierId: string, dossierName?: string) => void
  refreshFeatureGates: () => Promise<void>
  clearContext: () => void
}

export const useClientContext = create<ClientContextState>()(
  persist(
    (set, get) => ({
      activeClientId: null,
      activeClientName: null,
      activeDossierId: null,
      activeDossierName: null,
      activeRelationship: null,
      activeContract: null,
      featureGates: {},
      isLoadingClient: false,
      isLoadingFeatures: false,

      setActiveClient: async (clientId: string, clientName?: string) => {
        set({
          activeClientId: clientId,
          activeClientName: clientName || null,
          activeDossierId: null,
          activeDossierName: null,
          activeRelationship: null,
          activeContract: null,
          featureGates: {},
          isLoadingClient: true,
        })

        try {
          // Fetch client details if name not provided
          if (!clientName) {
            try {
              const { data: client } = await apiClient.get(`${CLIENT_MGMT_URL}/api/v1/clients/${clientId}`)
              set({ activeClientName: client.name })
            } catch {
              // Client name fetch is non-critical
            }
          }

          // Fetch relationship for this client
          const { data: client } = await apiClient.get(`${CLIENT_MGMT_URL}/api/v1/clients/${clientId}`)
          if (client?.relationship_id) {
            try {
              const { data: rel } = await apiClient.get(`${FEATURE_ENGINE_URL}/api/v1/relationships/${client.relationship_id}`)
              set({ activeRelationship: rel })

              // If relationship is active, fetch contract
              if (rel.status === 'ACTIVE' && rel.contract_id) {
                try {
                  const { data: contract } = await apiClient.get(`${FEATURE_ENGINE_URL}/api/v1/contracts/${rel.contract_id}`)
                  set({ activeContract: contract })
                } catch {
                  // Contract fetch non-critical
                }
              }

              // Evaluate features with relationship context
              await get().refreshFeatureGates()
            } catch {
              // Relationship fetch non-critical
            }
          }
        } catch (err) {
          console.error('Failed to load client context:', err)
        } finally {
          set({ isLoadingClient: false })
        }
      },

      setActiveDossier: (dossierId: string, dossierName?: string) => {
        set({
          activeDossierId: dossierId,
          activeDossierName: dossierName || null,
        })
      },

      refreshFeatureGates: async () => {
        const { activeRelationship } = get()
        set({ isLoadingFeatures: true })

        try {
          const params = new URLSearchParams({ plan_tier: '3' })
          if (activeRelationship?.id) {
            params.set('relationship_id', activeRelationship.id)
          }

          const { data } = await apiClient.get(
            `${FEATURE_ENGINE_URL}/api/v1/features/evaluate?${params}`
          )

          const gates: FeatureGateMap = {}
          if (data?.features) {
            for (const [code, feat] of Object.entries(data.features)) {
              const f = feat as { enabled: boolean; category: string; reason?: string }
              gates[code] = {
                enabled: f.enabled,
                category: f.category,
                reason: f.reason,
              }
            }
          }

          set({ featureGates: gates })
        } catch (err) {
          console.error('Failed to evaluate features:', err)
        } finally {
          set({ isLoadingFeatures: false })
        }
      },

      clearContext: () => {
        set({
          activeClientId: null,
          activeClientName: null,
          activeDossierId: null,
          activeDossierName: null,
          activeRelationship: null,
          activeContract: null,
          featureGates: {},
        })
      },
    }),
    {
      name: 'sgrc-adminclient-client-context',
      partialize: (state) => ({
        activeClientId: state.activeClientId,
        activeClientName: state.activeClientName,
        activeDossierId: state.activeDossierId,
        activeDossierName: state.activeDossierName,
      }),
    }
  )
)

/** Helper: check if a feature is enabled for the active client context */
export function useIsFeatureEnabled(featureCode: string): boolean {
  const { featureGates } = useClientContext()
  return featureGates[featureCode]?.enabled ?? false
}

/** Helper: check if a module has any enabled features */
export function useIsModuleEnabled(moduleCode: string): boolean {
  const { featureGates } = useClientContext()
  return Object.entries(featureGates).some(
    ([code, gate]) => code.startsWith(`${moduleCode}.`) && gate.enabled
  )
}
